/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { FormLabel, InlineLoading, Loading } from 'carbon-components-react';
import _ from 'lodash';
import moment from 'moment-timezone';
import React, { useState, useEffect, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useInterval } from 'react-use';

import { useAppDispatch, useAppSelector } from '@ibm-watson/shared/store';
import { ApiStatus, LoaderStates } from '@ibm-watson/shared/ts';
import {
  ServiceDeskLogo,
  getServiceDeskDescription
} from '../../../helpers/serviceDeskHelpers';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import {
  selectDraftIntegration,
  selectDraftIntegrationId
} from '../../../store/channels/selectors';
import {
  getIntelepeerNumber,
  getIntelepeerStatus
} from '../../../store/phone/actions';
import {
  selectIntelepeerState,
  selectIntelepeerStatus
} from '../../../store/phone/selectors';
import {
  FREE_PHONE_BYOSD_TYPE,
  INTELEPEER_BYOSD_TYPE
} from '../../../util/channelTypes';
import { PhoneQuickSetup } from '../new/messages';
import { TEN_SECONDS } from '../phone-number-tab/constants';
import { displayNum } from '../phone-number-tab/helpers';
import { PhoneNumberTab } from '../phone-number-tab/messages';
import { FIELDS } from '../schema';
import './Connections.scss';

export const FreePhoneNumber = () => {
  const dispatch = useAppDispatch();
  const { formatMessage: t } = useIntl();

  const formMethods = useFormContext();
  const { getFieldValue, updateFieldValue } = useFormHelpers(formMethods);

  const draftIntegration = useAppSelector(selectDraftIntegration);
  const integrationId = useAppSelector(selectDraftIntegrationId);
  const intelepeerNumStatus = useAppSelector(selectIntelepeerStatus);
  const intelepeerNumState = useAppSelector(selectIntelepeerState);

  const intelepeerNum = getFieldValue(FIELDS.PHONE_NUMBER_NUMBER(0));

  // This state variable is used to prevent the calling of subscribe
  // more than once
  const [intelepeerNumCreated, setIntelepeerNumCreated] = useState<boolean>(
    !_.isEmpty(intelepeerNum)
  );
  const [loaderMessage, setLoaderMessage] = useState<string>('');

  const displayedNum = !_.isNil(intelepeerNum) ? displayNum(intelepeerNum) : '';
  const minutesSinceCreated = moment().diff(
    _.get(draftIntegration, 'timestamp_created'),
    'minutes'
  );

  const intelepeerNumCheck = useCallback(async () => {
    // Check if IntelePeer number exists, if not create it
    // If it is IDLE, (what it looks like when first loaded) we'll
    // run right away because we haven't checked yet. Interval will
    // take over afterwards.
    if (intelepeerNum === undefined && !intelepeerNumCreated) {
      setIntelepeerNumCreated(true);
      const intelepeerConfig = await dispatch(
        getIntelepeerNumber(draftIntegration?.integration_id)
      );
      updateFieldValue(
        FIELDS.PHONE_NUMBERS,
        intelepeerConfig?.payload?.phone_numbers
      );
    }

    if (intelepeerNumStatus === ApiStatus.IDLE) {
      dispatch(getIntelepeerStatus(integrationId));
    }
  }, [
    draftIntegration,
    intelepeerNum,
    intelepeerNumCreated,
    intelepeerNumStatus,
    dispatch,
    updateFieldValue,
    integrationId
  ]);

  // Once the draft integration has loaded, check the status of the intelepeer num
  useEffect(() => {
    if (!_.isNil(draftIntegration)) {
      intelepeerNumCheck();
    }
  }, [draftIntegration, intelepeerNumCheck, intelepeerNumCreated]);

  // Start the interval if necessary to check the Status.
  useInterval(
    () => {
      dispatch(getIntelepeerStatus(integrationId));
    },
    intelepeerNumStatus === ApiStatus.LOADING ||
      intelepeerNumStatus === ApiStatus.IDLE
      ? TEN_SECONDS
      : null
  );

  useEffect(() => {
    let message = null;
    switch (intelepeerNumState) {
      case LoaderStates.ACTIVE:
        // Checks if phone integration is older than 5 minutes...
        // If 5 mins or older, active loader state message is checking instead of provisioning
        if (minutesSinceCreated >= 5) {
          message = t(PhoneNumberTab.intelepeerChecking);
        } else {
          message = t(PhoneNumberTab.intelepeerWaiting);
        }
        break;
      case LoaderStates.SUCCESS:
        message = t(PhoneNumberTab.intelepeerReady);
        break;
      case LoaderStates.ERROR:
        message = t(PhoneNumberTab.intelepeerNotReady);
        break;
      default:
        message = '';
    }
    setLoaderMessage(message);
  }, [
    draftIntegration,
    intelepeerNumState,
    minutesSinceCreated,
    t,
    updateFieldValue
  ]);

  return (
    <>
      <p className="TabContent__subtitle">
        {getServiceDeskDescription('phone', FREE_PHONE_BYOSD_TYPE, t)}
      </p>
      {(_.isEmpty(displayedNum) || !intelepeerNumCreated) && <Loading />}
      {intelepeerNum && !_.isEmpty(displayedNum) && (
        <div className="TabContent__field-container">
          <div className="PhoneProvider__number">{displayedNum}</div>
          <div className="PhoneProvider__note">
            <div className="PhoneProvider__supportIcon-status">
              <InlineLoading
                description={loaderMessage}
                status={intelepeerNumState}
              />
            </div>
            <div className="PhoneProvider__supportIcon-sponsor">
              <FormLabel>
                <FormattedMessage {...PhoneQuickSetup.freePhoneNote} />
              </FormLabel>
            </div>
            <ServiceDeskLogo
              className="PhoneProvider__supportIcon PhoneProvider__supportIcon-logo"
              serviceDeskType={INTELEPEER_BYOSD_TYPE}
              showTitle={false}
              size={16}
            />
          </div>
        </div>
      )}
    </>
  );
};
